<script setup lang="ts">
import ProgressFrame from '../layout/ProgressFrame.vue';
import AddIcon from '@shared/assets/icon/add.svg';
import ProjectTile from '../../components/ProjectTile.vue';
import ButtonPrimary from '@/shared/components/button/ButtonPrimary.vue';
import ButtonText from '@/shared/components/button/ButtonText.vue';
import router from '@/router';
import LoadingSpinner from '@/shared/components/LoadingSpinner.vue';
import { useProjectStore } from '../../stores/projectStore';
import { storeToRefs } from 'pinia';
import { onMounted, ref } from 'vue';
import PageLoadFailed from '../../components/PageLoadFailed.vue';

const projectStore = useProjectStore();
const { commissioningProjects: projects } = storeToRefs(projectStore);

const isLoading = ref(true);
const pageLoadFailed = ref(false);

onMounted(async () => {
  await fetchProjects();
});

const fetchProjects = async () => {
  isLoading.value = true;
  pageLoadFailed.value = false;

  try {
    await projectStore.fetchCommissioningProjects();
  } catch (error) {
    console.error('Failed to fetch projects', error);
    pageLoadFailed.value = true;
  } finally {
    isLoading.value = false;
  }
};

const goToAllProjects = () => {
  router.push({ name: 'ProjectListAll' });
};

const goToCreateProject = () => {
  projectStore.resetNewProject();
  router.push({ name: 'ProjectCreate' });
};
</script>

<template>
  <ProgressFrame pageClass="section-page">
    <template #title>Select a project</template>

    <template v-if="isLoading">
      <LoadingSpinner />
    </template>

    <template v-else-if="pageLoadFailed">
      <PageLoadFailed
        title="Failed to load projects"
        :retryButtonExtraTopMargin="true"
        @onRetry="fetchProjects"
      />
    </template>

    <template v-else>
      <div class="content">
        <template v-if="projects.length === 0">
          <p class="empty-project-list__text">
            You don't have any projects awaiting commissioning.
          </p>
        </template>

        <template v-else v-for="project in projects" :key="project.id">
          <ProjectTile :project="project" :canEdit="true" :canAddSite="true" />
        </template>
      </div>

      <div
        :class="['end-of-page', { float: projects.length > 0 }, { center: projects.length === 0 }]"
      >
        <ButtonPrimary
          class="create-project__button"
          block
          @click="goToCreateProject"
          data-tn="create-project-btn"
        >
          <template #iconLeft>
            <AddIcon />
          </template>
          Create project
        </ButtonPrimary>

        <span v-if="projects.length > 0" class="no-project-found__text">
          Don't see your project here?
        </span>

        <ButtonText
          class="see-all-projects__button"
          block
          @click="goToAllProjects"
          data-tn="see-all-projects-btn"
        >
          <span class="see-all-projects__button__text">View or edit all your projects</span>
        </ButtonText>
      </div>
    </template>
  </ProgressFrame>
</template>

<style lang="less" scoped>
.empty-project-list__text {
  text-align: center;
  color: @color-primary;
  font-weight: @font-weight-regular;
}

.end-of-page {
  margin-top: @margin-top-end-of-page-section;
  padding: @content-side-margin;
  width: 100%;

  .no-project-found__text {
    display: block;
    color: @color-primary;
    font-weight: @font-weight-regular;
    text-align: center;
  }

  .create-project__button,
  .see-all-projects__button {
    margin-bottom: @margin-bottom-button;
  }

  .see-all-projects__button__text {
    text-decoration: underline;
    font-weight: @font-weight-regular;
  }

  .see-all-projects__button {
    padding-top: 0;
    padding-bottom: 0;
    height: fit-content;
  }
}

.end-of-page.center {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  margin-top: 0;
}

.end-of-page.float {
  position: fixed;
  bottom: 0;

  background-color: @color-background-light;
  border-top: 1px solid @color-mid-tone;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
}

// Give the content a fixed bottom padding to prevent the buttons from being hidden by the bottom of the screen.
.content {
  padding-bottom: 100px;
}
</style>
